import { AxiosError } from "axios";
import { returnDecrypt } from "../../helpers";
import { magnoApi } from "./axios";
import { useRouterStore } from "../../stores";

interface ResponseLoginMagnoSriI {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: Date;
  user: any;
}

export const getErrorMessage = (err: unknown): string => {
  const axiosErr = err as AxiosError;
  const data = axiosErr?.response?.data as
    | { message?: string; error?: string }
    | undefined;
  return data?.error ?? data?.message ?? axiosErr?.message ?? "Error desconocido";
};

// Guarda la sesión en el store de forma centralizada
const guardarSesion = (data: ResponseLoginMagnoSriI) => {
  const { setTokenMagnoSri, setRefreshTokenMagnoSri, setExpiresAtMagnoSri } =
    useRouterStore.getState();
  setTokenMagnoSri(data.accessToken);
  setRefreshTokenMagnoSri(data.refreshToken);
  // expiresAt puede venir como Date o string; lo normalizamos a ISO
  setExpiresAtMagnoSri(new Date(data.expiresAt).toISOString());
};

// ¿El token caducó o está por caducar? (margen de 30s)
const tokenCaducado = (): boolean => {
  const { expiresAtMagnoSri } = useRouterStore.getState();
  if (!expiresAtMagnoSri) return true;
  const expira = new Date(expiresAtMagnoSri).getTime();
  const ahora = Date.now() + 30_000; // 30s de margen
  return ahora >= expira;
};

// Login con email/password
const login = async (): Promise<string> => {
  const { userMagnoSri, passwordMagnoSri } = useRouterStore.getState();
  if (!userMagnoSri || !passwordMagnoSri) {
    throw new Error("Falta datos para el login");
  }
  const email = returnDecrypt(userMagnoSri);
  const password = returnDecrypt(passwordMagnoSri);

  const { data } = await magnoApi.post<ResponseLoginMagnoSriI>("/auth/login", {
    email,
    password,
  });
  guardarSesion(data);
  return data.accessToken;
};

// Refresca usando el refreshToken; si falla, cae a login
const refresh = async (): Promise<string> => {
  const { refreshTokenMagnoSri } = useRouterStore.getState();
  if (!refreshTokenMagnoSri) return login();

  try {
    const { data } = await magnoApi.post<ResponseLoginMagnoSriI>(
      "/auth/refresh",
      { refreshToken: refreshTokenMagnoSri },
    );
    guardarSesion(data);
    return data.accessToken;
  } catch {
    // refreshToken inválido/caducado → login completo
    return login();
  }
};

// Evita refrescos simultáneos: si ya hay uno en curso, reusa la promesa
let refreshEnCurso: Promise<string> | null = null;

export const renovarToken = (): Promise<string> => {
  if (!refreshEnCurso) {
    refreshEnCurso = refresh().finally(() => {
      refreshEnCurso = null;
    });
  }
  return refreshEnCurso;
};

// Devuelve un token válido: login si no hay, renueva si caducó, reusa si sirve
export const obtenerTokenValido = async (): Promise<string> => {
  const { tokenMagnoSri } = useRouterStore.getState();
  if (!tokenMagnoSri) return renovarToken(); // sin token → login/refresh
  if (tokenCaducado()) return renovarToken(); // caducado → renovar
  return tokenMagnoSri; // válido → reusar
};

// Headers de autenticación hacia Magno. Los clientes usan su API key
// (X-API-Key: sin login ni tokens que renovar); el superadmin, que no
// pertenece a un tenant y por tanto no tiene key, sigue con el login de
// plataforma.
export const obtenerHeadersMagno = async (): Promise<Record<string, string>> => {
  const { apiKeyMagnoSri } = useRouterStore.getState();
  if (apiKeyMagnoSri) return { "X-API-Key": returnDecrypt(apiKeyMagnoSri) };
  return { Authorization: `Bearer ${await obtenerTokenValido()}` };
};
